import { PaytrieAPI } from '@paytrie/client'

import { ImageData } from './signup'
import { dataUrlToBlob } from '@/utils/file'

type UploadDocument =
  PaytrieAPI.CreateUserKycDocumentUploadUrlsBodyDocumentsItem

const isValidContentType = (
  type: string
): type is UploadDocument['contentType'] =>
  type in PaytrieAPI.CreateUserKycDocumentUploadUrlsBodyDocumentsItemContentType

export async function uploadFile(userId: string, files: ImageData[]) {
  try {
    const unsupportedFile = files.find((file) => !isValidContentType(file.type))
    if (unsupportedFile) {
      return {
        error: new Error(`Unsupported file type: ${unsupportedFile.type}`),
      }
    }

    const blobs = files.map((file) => dataUrlToBlob(file.url))

    const documents: UploadDocument[] = files.map((file, index) => ({
      name: file.name,
      contentType: file.type as UploadDocument['contentType'],
      size: blobs[index].size,
    }))

    const urlsRes = await PaytrieAPI.createUserKycDocumentUploadUrls(userId, {
      documents,
    })
    if (urlsRes.status !== 201) {
      return {
        error: new Error(`Failed to request upload URLs (${urlsRes.status})`),
      }
    }

    const { uploads } = urlsRes.data.data

    await Promise.all(
      uploads.map(async (upload, index) => {
        const res = await fetch(upload.uploadUrl, {
          method: 'PUT',
          headers: upload.requiredHeaders,
          body: blobs[index],
        })
        if (!res.ok) {
          throw new Error(`File upload to storage failed (${res.status})`)
        }
      })
    )

    const finalizeRes = await PaytrieAPI.finalizeUserKycDocuments(userId, {
      objectNames: uploads.map((upload) => upload.objectName),
    })
    if (finalizeRes.status !== 201) {
      return {
        error: new Error(`Failed to finalize upload (${finalizeRes.status})`),
      }
    }

    return { response: finalizeRes }
  } catch (error) {
    return { error }
  }
}
